import { Checkbox, FormGroup, Label, Modal, SubmitButton, Tooltip } from "@xola/ui-kit";
import { FormField, PurchaseItemStatus, TextareaField } from "common";
import { FormikHelpers } from "formik";
import React, { useState } from "react";
import { Event, MessageTemplate, Purchase, PurchaseItem, Timeslot } from "types";
import { useCollection } from "../../hooks/useCollection";
import { useInvalidate } from "../../hooks/useInvalidation";
import { useResource } from "../../hooks/useResource";
import { TemplateSelect } from "../Common/MessageModal/TemplateSelect";
import { htmlToDisabledHtml } from "../Common/RichEditor/RichEditor";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { EventSummaryTile } from "../Event/EventSummaryTile";
import useAcceptOrDeclinePurchaseMutation, {
    AcceptOrDeclinePurchaseValues,
} from "./AcceptOrDeclinePurchaseModal/useAcceptOrDeclinePurchaseMutation";
import { isTableView } from "@/stores/dashboardFilterStore";

interface PendingPurchaseModalProps {
    purchase: Purchase;
    item: PurchaseItem;
    isOpen: boolean;
    onClose: () => void;
    timeslot?: Timeslot;
    onSuccess?: (purchaseId: string) => void;
}

type FormState = AcceptOrDeclinePurchaseValues;

export const PendingPurchaseModal = ({
    purchase,
    item,
    isOpen,
    onClose,
    timeslot,
    onSuccess,
}: PendingPurchaseModalProps) => {
    return (
        <Modal isOpen={isOpen} className="overflow-visible" onClose={onClose}>
            <ModalContent purchase={purchase} item={item} timeslot={timeslot} onClose={onClose} onSuccess={onSuccess} />
        </Modal>
    );
};

interface ModalContentProps {
    purchase: Purchase;
    item: PurchaseItem;
    onClose: () => void;
    timeslot?: Timeslot;
    onSuccess?: (purchaseId: string) => void;
}

const ModalContent = ({ purchase, item, onClose, timeslot, onSuccess }: ModalContentProps) => {
    const invalidate = useInvalidate();
    const { data: templates } = useCollection<MessageTemplate>("messageTemplates");
    const { data: event, isLoading: isLoadingEvent } = useResource<Event>(["events", timeslot?.event.id]);

    const emailTemplates = templates?.filter((template) => template.email?.body);
    const [emailTemplate, setEmailTemplate] = useState<MessageTemplate | null>(null);

    const lineItem = purchase.lineItems.find((lineItem) => {
        return lineItem.type === "payment" && lineItem?.payment?.method === "cc" && lineItem.status === "pending";
    });
    const showDoNotChargeNow = !!lineItem; // alternatively !(typeof lineItem === "undefined")

    const { mutateAsync: processPurchase, isLoading } = useAcceptOrDeclinePurchaseMutation(purchase, item);

    const initialValues: FormState = {
        email: { subject: "", body: "" },
        sms: { body: "" },
        enableEmail: !!purchase.customerEmail,
        enableSms: false,
        status: undefined,
        collectPayment: false,
    };

    const handleSubmit = async (values: any, { setFieldError }: FormikHelpers<FormState>) => {
        const { status } = values;
        const message = values.email.body ?? "";
        if (status && status === PurchaseItemStatus.DECLINED && message.length === 0) {
            setFieldError("email.body", "is required");
            return;
        }

        await processPurchase(values);

        onSuccess?.(purchase.id);
        if (!isTableView()) {
            setTimeout(() => {
                invalidate("timeslots");
                onClose();
            }, 1000); // give some time for elastic search to get updated and invalidate to get new data
        }
    };

    return (
        <XolaFormik
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, values, setFieldValue }) => (
                <>
                    <Modal.Header>Accept or Decline Booking</Modal.Header>

                    <Modal.Body>
                        <fieldset disabled={isLoading}>
                            {!isLoadingEvent && event ? <EventSummaryTile event={event} /> : null}
                        </fieldset>
                        <FormGroup>
                            <Label>Load Template</Label>
                            <TemplateSelect
                                className="flex-1"
                                value={emailTemplate}
                                templates={emailTemplates}
                                dataTestId="message-modal-template-select"
                                onChange={(template) => {
                                    setEmailTemplate(template);
                                    const body = template?.email?.body ?? "";
                                    setFieldValue("email.body", body);
                                }}
                            />
                        </FormGroup>

                        {emailTemplate ? (
                            <div
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                    __html: htmlToDisabledHtml(values?.email?.body ?? ""),
                                }}
                                // TODO: See HtmlMessageTemplate try dompurify
                                className="mb-4 rounded border border-gray-light bg-gray-lighter py-2.5 px-3 text-gray hover:border-black"
                            />
                        ) : (
                            <FormField label="Custom Message" name="email.body">
                                <TextareaField className="h-28" placeholder="Message" disabled={!!emailTemplate} />
                            </FormField>
                        )}

                        {showDoNotChargeNow && (
                            <Tooltip
                                content="Checking this box lets you accept this booking now, but charge the customer's credit card at a later time"
                                placement="auto"
                            >
                                <Checkbox
                                    label="Do not charge credit card now"
                                    onChange={(e: any) => setFieldValue("collectPayment", e.target.checked)}
                                />
                            </Tooltip>
                        )}
                    </Modal.Body>

                    <Modal.Footer>
                        <fieldset className="flex justify-end space-x-4" disabled={isLoading}>
                            <SecondaryButton onClick={onClose}>Later</SecondaryButton>

                            <SubmitButton
                                type="button"
                                color="danger"
                                disabled={isLoading}
                                isLoading={isLoading && values.status === PurchaseItemStatus.DECLINED}
                                onClick={() => {
                                    setFieldValue("status", PurchaseItemStatus.DECLINED);
                                    handleSubmit();
                                }}
                            >
                                Decline
                            </SubmitButton>

                            <SubmitButton
                                type="button"
                                color="success"
                                disabled={isLoading}
                                isLoading={isLoading && values.status === PurchaseItemStatus.CONFIRMED}
                                onClick={() => {
                                    setFieldValue("status", PurchaseItemStatus.CONFIRMED);
                                    handleSubmit();
                                }}
                            >
                                Accept
                            </SubmitButton>
                        </fieldset>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};
